import React from "react";
import { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import DashboardLayout from "../../layouts/DashboardLayout";
import DataTable from "../../components/ui/DataTable";
import { useRequisitionHistory } from "../../hooks/useWorkflows";
import { useNavigate } from "react-router-dom";

function MyRequisitionsHistoryPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [workflowTypeFilter, setWorkflowTypeFilter] = useState("");
  const [statusFilter] = useState("");

  const filters = useMemo(() => {
    const base = {
      page,
      limit: pageSize,
    };

    if (search) {
      base.search = search;
    }

    if (workflowTypeFilter) {
      base.workflowType = workflowTypeFilter;
    }

    if (statusFilter) {
      base.status = statusFilter;
    }

    return base;
  }, [page, pageSize, search, workflowTypeFilter, statusFilter]);

  const { workflows, pagination, isLoading, isError, error } =
    useRequisitionHistory(filters);

  const getWorkflowTypeLabel = (type) => {
    const labels = {
      imprest: "Imprest",
      retirement: "Retirement",
      petty_cash: "Petty Cash",
      purchase_requisition: "Purchase Requisition",
      claim: "Claim",
      lpo: "Local Purchase Order",
      payment_voucher: "Payment Voucher",
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: "Draft",
      pending_verification: "Pending Verification",
      in_verification: "In Verification",
      verified: "Verified",
      pending_approval: "Pending Approval",
      approved: "Approved",
      rejected: "Rejected",
      canceled: "Canceled",
      completed: "Completed",
      archived: "Archived",
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: "bg-slate-100 text-slate-700 border-slate-200",
      pending_verification: "bg-yellow-50 text-yellow-700 border-yellow-200",
      in_verification: "bg-blue-50 text-blue-700 border-blue-200",
      verified: "bg-green-50 text-green-700 border-green-200",
      pending_approval: "bg-orange-50 text-orange-700 border-orange-200",
      approved: "bg-green-50 text-green-700 border-green-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
      canceled: "bg-slate-100 text-slate-600 border-slate-200",
      completed: "bg-green-50 text-green-700 border-green-200",
      archived: "bg-slate-100 text-slate-600 border-slate-200",
    };
    return classes[status] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getPaymentStatus = (payment) => payment?.status || "not_required";

  const getPaymentStatusLabel = (paymentStatus) => {
    const labels = {
      paid: "Paid",
      pending: "Payment Pending",
      not_required: "Payment Not Required",
    };
    return labels[paymentStatus] || paymentStatus;
  };

  const getPaymentBadgeClass = (paymentStatus) => {
    const classes = {
      paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
      pending: "bg-orange-50 text-orange-700 border-orange-200",
      not_required: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return (
      classes[paymentStatus] || "bg-slate-100 text-slate-700 border-slate-200"
    );
  };

  const getRetirementStatus = (retirement) =>
    retirement?.status || "not_started";

  const getRetirementStatusLabel = (status) => {
    const labels = {
      not_started: "Retirement Not Started",
      pending: "Pending Retirement",
      submitted: "Retirement Submitted",
      completed: "Retirement Completed",
      rejected: "Retirement Rejected",
    };
    return labels[status] || status;
  };

  const getRetirementBadgeClass = (status) => {
    const classes = {
      not_started: "bg-slate-100 text-slate-700 border-slate-200",
      pending: "bg-orange-50 text-orange-700 border-orange-200",
      submitted: "bg-blue-50 text-blue-700 border-blue-200",
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
    };
    return classes[status] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "—";
    try {
      return new Date(dateValue).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const formatCurrency = (amount, currency = "TZS") => {
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: currency || "TZS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getRetirementSummaryMeta = (summary) => {
    if (
      !summary ||
      !summary.requiresRetirement ||
      summary.paymentStatus !== "paid"
    )
      return null;
    if (summary.status === "completed") return null;

    if (summary.endDateMissing) {
      return {
        label: "Return date missing — retirement window cannot be tracked",
        className: "bg-red-50 text-red-700 border-red-200",
      };
    }

    if (!summary.endDateReached) {
      return {
        label: `Return on ${formatDate(summary.endDate)} • Due ${formatDate(summary.dueDate)}`,
        className: "bg-slate-100 text-slate-700 border-slate-200",
      };
    }

    if (summary.isOverdue) {
      const overdueDays = summary.daysSinceEndDate ?? 0;
      return {
        label: `Retirement overdue by ${overdueDays} day${overdueDays === 1 ? "" : "s"} (due ${formatDate(summary.dueDate)})`,
        className: "bg-red-50 text-red-700 border-red-200",
      };
    }

    const remaining = summary.daysRemaining ?? 0;
    const className =
      remaining > 3
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-orange-50 text-orange-700 border-orange-200";

    return {
      label: `${remaining} day${remaining === 1 ? "" : "s"} left to retire (due ${formatDate(summary.dueDate)})`,
      className,
    };
  };

  const getWorkflowRoute = (type) => {
    const routes = {
      imprest: "/dashboard/requests/imprest",
      retirement: "/dashboard/requests/retirement",
      petty_cash: "/dashboard/requests/petty-cash",
      purchase_requisition: "/dashboard/requests/purchase-requisition",
      claim: "/dashboard/requests/claim",
      lpo: "/dashboard/requests/lpo",
      payment_voucher: "/dashboard/requests/payment-voucher",
    };
    return routes[type] || "/dashboard/requests";
  };

  const columns = [
    {
      key: "request",
      header: "Request",
      render: (workflow) => {
        const projectCode =
          workflow.formData?.projectData?.project?.projectCode;
        return (
          <div className="flex min-w-[180px] flex-col gap-0.5 whitespace-nowrap">
            <span className="font-mono text-xs font-semibold text-slate-800">
              {workflow.workNumber || "—"}
            </span>
            <span className="text-xs font-medium text-slate-600">
              {getWorkflowTypeLabel(workflow.workflowType)}
            </span>
            {projectCode && (
              <span className="text-[11px] text-slate-500">{projectCode}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "requestedBy",
      header: "Requested By",
      render: (workflow) => {
        const initiator = workflow.initiator;
        const name =
          initiator?.fullName ||
          [initiator?.firstName, initiator?.lastName]
            .filter(Boolean)
            .join(" ") ||
          "—";

        return (
          <div className="flex min-w-[160px] flex-col gap-0.5 whitespace-nowrap">
            <span className="text-xs font-medium text-slate-800">{name}</span>
            {initiator?.email && (
              <span className="text-[11px] text-slate-500">
                {initiator.email}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "formData",
      header: "Description",
      render: (workflow) => {
        const description = workflow.formData?.description;
        return (
          <p className="text-xs text-slate-800 line-clamp-2">
            {description || "—"}
          </p>
        );
      },
    },
    {
      key: "totalAmount",
      header: "Amount",
      render: (workflow) => (
        <span className="font-medium text-slate-700">
          {formatCurrency(workflow.totalAmount, workflow.formData?.currency)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (workflow) => {
        const paymentStatus = getPaymentStatus(workflow.payment);
        const retirementStatus = getRetirementStatus(workflow.retirement);
        const retirementSummaryMeta = getRetirementSummaryMeta(
          workflow.retirementSummary,
        );

        return (
          <div className="flex min-w-[220px] max-w-[260px] flex-col items-start gap-1.5 whitespace-nowrap">
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0 text-[9px] font-semibold leading-tight ${getStatusBadgeClass(
                workflow.status,
              )}`}
            >
              {getStatusLabel(workflow.status)}
            </span>

            <div className="flex items-center gap-1.5">
              {paymentStatus === "paid" && (
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0 text-[9px] font-semibold leading-tight ${getPaymentBadgeClass(
                    paymentStatus,
                  )}`}
                >
                  {getPaymentStatusLabel(paymentStatus)}
                </span>
              )}

              {paymentStatus === "paid" && (
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0 text-[9px] font-semibold leading-tight ${getRetirementBadgeClass(
                    retirementStatus,
                  )}`}
                >
                  {getRetirementStatusLabel(retirementStatus)}
                </span>
              )}
            </div>

            {retirementSummaryMeta && (
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0 text-[9px] font-semibold leading-tight ${retirementSummaryMeta.className}`}
              >
                {retirementSummaryMeta.label}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (workflow) => (
        <div className="flex flex-col items-end gap-1 whitespace-nowrap">
          <button
            type="button"
            onClick={() =>
              navigate(
                `${getWorkflowRoute(workflow.workflowType)}/${workflow._id}`,
              )
            }
            className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-white px-2 py-1 text-[10px] font-semibold leading-tight text-primary transition hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
            aria-label={`View ${getWorkflowTypeLabel(workflow.workflowType)}`}
          >
            <svg
              className="h-3 w-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View
          </button>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Requisitions · My Requisitions History">
      <Helmet>
        <title>Globotech ERP | Requisitions · My Requisitions History</title>
      </Helmet>
      <div className="rounded-3xl border border-cream/60 bg-white shadow-sm">
        <div className="border-b border-cream/60 px-6 py-4">
          <p className="text-base font-semibold text-primary">
            My Requisitions History
          </p>
          <p className="mt-1 text-sm text-slate-500">
            View all requisitions that you have verified or approved.
          </p>
        </div>
        <div className="px-6 py-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div>
                <label
                  htmlFor="workflow-type-filter"
                  className="block text-xs font-semibold uppercase tracking-[0.2em] text-primary"
                >
                  Type
                </label>
                <select
                  id="workflow-type-filter"
                  value={workflowTypeFilter}
                  onChange={(e) => {
                    setWorkflowTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  className="mt-1 w-40 rounded-lg border border-cream/60 bg-white px-3 py-2 text-sm text-slate-700 outline-none ring-primary/30 transition focus:border-primary focus:ring"
                >
                  <option value="">All Types</option>
                  <option value="imprest">Imprest</option>
                  <option value="retirement">Retirement</option>
                  <option value="petty_cash">Petty Cash</option>
                  <option value="purchase_requisition">
                    Purchase Requisition
                  </option>
                  <option value="claim">Claim</option>
                  <option value="lpo">Local Purchase Order</option>
                  <option value="payment_voucher">Payment Voucher</option>
                </select>
              </div>

        

              <div className="sm:ml-3">
                <label
                  htmlFor="search"
                  className="block text-xs font-semibold uppercase tracking-[0.2em] text-primary"
                >
                  Search
                </label>
                <input
                  id="search"
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search requisitions..."
                  className="mt-1 w-64 rounded-lg border border-cream/60 bg-white px-3 py-2 text-sm text-slate-700 outline-none ring-primary/30 transition focus:border-primary focus:ring"
                />
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            rows={workflows}
            isLoading={isLoading}
            isError={isError}
            error={error}
            emptyTitle="No requisitions"
            emptyMessage="You don't have any requisitions. Create a new requisition to get started."
            emptyIcon={
              <svg
                className="h-16 w-16"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 8h36v48H14V8z"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 16h28M18 24h28M18 32h20"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            }
            pagination={{
              currentPage: pagination.currentPage,
              totalPages: pagination.totalPages,
              totalItems: pagination.totalItems,
              pageSize: pagination.pageSize,
              hasNextPage: pagination.hasNextPage,
              hasPrevPage: pagination.hasPrevPage,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default MyRequisitionsHistoryPage;
